import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ColumnProcessProps,
  FileAnalysis,
  FileDataQuantity,
} from 'src/presentation/types/interfaces/IImport.interface';

import { Step, StepProps } from '@/lib/components/Step/Step';
import { RouteEnum } from '@/lib/components/TopBar/routeEnum';
import { Button, Card, CardSection, IconSvg, toast } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';
import Select, { Option } from '@/lib/v2/components/Select/Select';
import Text from '@/lib/v2/components/Text/Text';
import { UploadZone } from '@/lib/v2/examples';
import { ExportUserIcon } from '@/lib/v2/icons/outline';
import { UploadArrowIcon } from '@/lib/v2/icons/solid';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import {
  ContactsMappingField,
  IMappingField,
  ITableMappingField,
  matchValueSelector,
  wrappingMappingData,
} from '@/src/ContactsModule/components/ContactsMappingField/ContactsMappingField';
import { SelectorEnum } from '@/src/ContactsModule/components/ContactsMappingField/selectorsValues';
import {
  ContactsDetailTable,
  IDetailViewList,
} from '@/src/ContactsModule/components/ContactsViewInfo/ContactsDetailTable';
import { ContactsViewInfo } from '@/src/ContactsModule/components/ContactsViewInfo/ContactsViewInfo';
import { velocityBar } from '@/src/ContactsModule/components/ContactsViewInfo/ProgressVelocityEnum';
import { ContactsTagsAndGroups } from '@/src/ContactsModule/components/ContactTagsAndGroups/ContactsTagsAndGroups';
import { ImportSuccessModal } from '@/src/ContactsModule/components/ImportModal/ImportSuccessModal';
import { MappedFieldModal } from '@/src/ContactsModule/components/ImportModal/MappedFieldsModal';
import {
  ImportRecentProps,
  ImportRecentTable,
} from '@/src/ContactsModule/components/ImportRecentTable/ImportRecentTable';
import {
  useMutationFileContext,
  useStateFileContext,
} from '@/src/ContactsModule/contexts/ImportFileContext';
import { getDataCard } from '@/src/ContactsModule/contexts/ImportFileContext/DataCard';
import {
  useMutationLoadingBar,
  useMutationVerifyState,
} from '@/src/ContactsModule/contexts/ImportFileContext/ImportFileContext';
import { convertDataToViewInfo } from '@/src/ContactsModule/utils/ConvertDataToViewInfo';
import { validateStateFile } from '@/src/ContactsModule/utils/enums/ValidateStateFile.Enum';
import { modifyFile, validateFile } from '@/src/ContactsModule/utils/File';
import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { IEmblueService } from '@/src/infrastructure/Protocol/IEmblueService';
import { useEmblue, useService } from '@/src/infrastructure/Protocol/useEmblue';
import { useUserData } from '@/src/infrastructure/services/useUserData';
import { loadingStatus } from '@/src/presentation/types/enum/importStatus.enum';

import { UploadProgress } from './components';
import { ImportSteps } from './ImportStepEnum.enum';
import { StateOption } from './StateOptionsEnum.enum';
import { CustomsFieldsProps } from './V2ImportTypes';

import { SectionHeader } from '@/modules/ContactsModule/components';

const dropConfig = {
  noClick: false,
  maxFiles: 1,
  maxSize: 100 * 1024 * 1024, // 100 MB * 1024 => KB *1024 => bytes
  accept: 'text/csv',
  multiple: false,
};

export type selectedGroupProps = {
  ListIdSelected: number[];
  ListNameGroups: string[];
};

const delimiterList: Option[] = [
  {
    id: 0,
    name: 'Comma',
    value: ',',
  },
  {
    id: 1,
    name: 'Semicolon',
    value: ';',
  },
  {
    id: 2,
    name: 'Tab Space',
    value: 't',
  },
  {
    id: 3,
    name: 'Vertical Bar (|)',
    value: '|',
  },
];

export const ImportMain = () => {
  const [uploadPercentage, setUploadPercentage] = useState(0);

  const [isDraged, setIsDraged] = useState(false);
  const [fileSuccess, setFileSuccess] = useState(false);
  const [step1Progress, setStep1Progress] = useState<
    'readyToSelectFile' | 'uploadingFile' | 'mappingFields' | 'mappingFieldsEnd'
  >('readyToSelectFile');
  const { files, fileName } = useStateFileContext();
  const { setFiles, setFileName } = useMutationFileContext();
  const { verifyImportState } = useMutationVerifyState();
  const { loadingBar } = useMutationLoadingBar();
  const { t } = useTranslation();
  const recentImportIsEnabled = useFeatureFlag('recentImportIsEnabled');
  const [selectListCustomField, setSelectListCustomField] = useState<Option[]>(
    files?.selectListFields ?? []
  );
  const [mappingList, setMappingList] = useState<IMappingField>({
    mappingList: files?.mappingList?.mappingList ?? [],
  });
  const [fileDataQuantity, setFileDataQuantity] = useState<FileDataQuantity>({
    quantityColumns: files?.quantityColumns as number,
    quantityContacts: files?.quantityContacts as number,
  });
  const [selectedGroups, setSelectedGroups] = useState<Option[]>([]);

  const [detailList, setDetailList] = useState<IDetailViewList[]>();
  const [isOpenModalSuccess, setIsOpenModalSuccess] = useState<boolean>(false);

  const [listCustomField] = useEmblue(ServiceMethods.getListCustomFields);
  const [importRecentFiles] = useEmblue(ServiceMethods.importRecent);
  const [delimiter, setDelimiter] = useState<Option>(delimiterList[0]);
  const [isOpenModalMappedFields, setIsOpenModalMappedFields] = useState<boolean>(false);
  const [userData] = useUserData();

  const service = useService() as IEmblueService;
  const navigate = useNavigate();

  const noImport = useMemo(
    () => ({
      id: StateOption.noImport,
      name: t('IMPORT_MAIN.noImport'),
      value: t('IMPORT_MAIN.noImport'),
    }),
    [t]
  );

  const resetStep1Loader = useCallback(() => {
    setStep1Progress('readyToSelectFile');
    setUploadPercentage(0);
  }, []);

  // //TO DO: remove method
  // const simulateUpload = () => {
  //   let progress = 0;
  //   setStep1Progress('uploadingFile');
  //   const interval = setInterval(() => {
  //     progress += 5;
  //     setUploadPercentage(progress);

  //     if (progress >= 100) {
  //       clearInterval(interval);
  //     }
  //   }, 200);
  // };

  // const simulateMappingAwait = () => {
  //   setTimeout(() => {
  //     setStep1Progress('mappingFieldsEnd');
  //   }, 2000);
  // };

  const steps = [
    { id: '01', name: t('IMPORT_MAIN.import') },
    { id: '02', name: t('IMPORT_MAIN.mappingField') },
    { id: '03', name: t('IMPORT_MAIN.groups') },
    { id: '04', name: t('IMPORT_MAIN.info') },
  ] as Array<StepProps>;

  const onDrop = async <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
    setIsDraged(false);
    const acceptFile = acceptedFiles.at(0);
    try {
      if (acceptFile) {
        await validateFile(acceptFile);
        setFiles({ ...files, file: acceptFile, step: ImportSteps.import });
        setFileName(acceptFile.name);
        setFileSuccess(true);
        datadogRum.addAction('ContactsImport.onDropFileSuccess', {
          email: userData.email,
          accountId: Number(userData.companyId),
        });
      } else if (fileRejections.length) {
        let errorType = 'unknown';
        fileRejections.at(0)?.errors.forEach((element: { code: string; message: string }) => {
          if (element.code === 'file-invalid-type') {
            toast({
              id: 'invalid-file-notification',
              title: t('IMPORT_MAIN.invalidFile'),
              variant: 'error',
              body: t('IMPORT_MAIN.invalidFileBody'),
            });
          } else if (element.code === 'file-too-large') {
            toast({
              id: 'invalid-file-notification',
              title: t('IMPORT_MAIN.invalidFile'),
              variant: 'error',
              body: t('IMPORT_MAIN.overSizeFile'),
            });
          }
          errorType = element.code;
        });
        setFiles({ ...files, fileRejections: fileRejections.at(0) });
        datadogRum.addAction('ContactsImport.onDropFileError', {
          email: userData.email,
          accountId: Number(userData.companyId),
          errorType,
        });
      }
    } catch (error) {
      console.error(error);
      let errorType = 'unknown';
      if (error === validateStateFile.CharacterNotAllowed) {
        toast({
          id: 'invalid-file-notification',
          title: t('IMPORT_MAIN.invalidFile'),
          variant: 'error',
          body: t('IMPORT_MAIN.characterNotAllowed'),
        });
        errorType = validateStateFile.CharacterNotAllowed;
      } else if (error === validateStateFile.fileEmpty) {
        toast({
          id: 'invalid-file-notification',
          title: t('IMPORT_MAIN.invalidFile'),
          variant: 'error',
          body: t('IMPORT_MAIN.fileEmpty'),
        });
        errorType = validateStateFile.fileEmpty;
      }
      datadogRum.addAction('ContactsImport.onDropFileError', {
        email: userData.email,
        accountId: Number(userData.companyId),
        errorType,
      });
    }
  };

  const handleUploadProgress = useCallback((percentCompleted: number) => {
    setUploadPercentage(percentCompleted);
  }, []);

  const importConfigurationProcess = useCallback(async () => {
    const fileNameSelected = files?.file?.name ?? fileName;
    if (!fileNameSelected) return;
    setStep1Progress('mappingFields');
    const { success, data } = await service.getImportConfiguration({
      fileName: fileNameSelected,
    });

    resetStep1Loader();

    if (!success || !data.onSuccess) {
      if (data.onError?.parseError) {
        const { LineError, Delimiter } = data.onError.parseError;
        toast({
          id: 'invalid-file-notification',
          title: t('IMPORT_MAIN.invalidFile'),
          body: t('IMPORT_MAIN.parseError', { lineError: LineError, delimiter: Delimiter }),
          variant: 'error',
          autoCloseDelay: 10000,
        });
      } else {
        toast({
          id: 'mapping-fields-notification-error',
          title: t('MAPPING_NOTIFICATION.errorTitle'),
          body: t('MAPPING_NOTIFICATION.errorBody'),
          variant: 'error',
        });
      }
      // could be a mappingFieldsError to show animation error
      // resetStep1Loader()
      return;
    }

    const configColumn = data.onSuccess;
    const { quantityColumns, quantityContacts } = configColumn;

    if (quantityContacts === 0) {
      toast({
        id: 'import-files-notification-error',
        title: t('MAPPING_NOTIFICATION.errorTitle'),
        body: t('IMPORT_MAIN.fileEmpty'),
        variant: 'error',
      });
      // could be a mappingFieldsError to show animation error
      // resetStep1Loader()
      return;
    }
    // at this point mapping was successful
    // setStep1Progress('mappingFieldsEnd');
    // setStep1Progress('readyToSelectFile');

    if (configColumn && listCustomField) {
      const suggestedDelimiter = delimiterList.find(
        (delimited: Option) => configColumn?.suggestedDelimiter === delimited.value
      );

      if (suggestedDelimiter) {
        setDelimiter(suggestedDelimiter);
      }
      const mappingField: IMappingField = wrappingMappingData(listCustomField, configColumn);
      // cspell:disable
      const disabledFieldNames = [
        'nps',
        'recency',
        'frequency',
        'amount',
        'eCommerce Classification',
        'fecha_creacion',
        'ultima_apertura',
        'ultimo_viral',
        'ultimo_click',
        'ultimo_viral',
        'ultima_actividad',
        'ultima_visita_web',
        'ultimo_tag_web',
        'ultimo_item_web',
        'ultima_suscripcion_push',
        'ultimo_envio_push',
        'ultimo_envio_sms',
      ];
      // cspell:enable

      const importArray = [
        noImport,
        ...listCustomField
          .filter((item) => {
            return !disabledFieldNames.includes(item.name);
          })
          .map((element: CustomsFieldsProps) => {
            return { id: element.id, name: element.name, value: element.name };
          }),
      ];
      setMappingList({ mappingList: [...mappingField.mappingList] });
      setFileDataQuantity({ quantityContacts, quantityColumns });
      setSelectListCustomField(importArray);
      setFiles({
        ...files,
        step: ImportSteps.mappingField,
        selectListCustomField: importArray,
        quantityColumns,
        quantityContacts,
      });
    }
  }, [fileName, files, listCustomField, noImport, resetStep1Loader, service, setFiles, t]);

  const importFile = useCallback(async () => {
    if (!files?.file) {
      // recentImportTable case
      if (fileName) {
        await importConfigurationProcess();
      }
      return;
    }
    const formData = new FormData();
    const file = files.file;

    if (!file) return;

    formData.append('Filedata', file);
    let fileUploader: string | undefined;

    if (!files?.mappingList) {
      toast({
        id: 'mapping-fields-notification-success',
        title: t('MAPPING_NOTIFICATION.title'),
        body: t('MAPPING_NOTIFICATION.body'),
        variant: 'success',
      });
      try {
        setFileSuccess(false);
        setStep1Progress('uploadingFile');
        fileUploader = (await service.fileUploader({ formData }, handleUploadProgress))?.replace(
          '/',
          ''
        );
      } catch (error) {
        console.error(error);
        toast({
          id: 'mapping-fields-notification-error',
          title: t('MAPPING_NOTIFICATION.errorTitle'),
          body: t('MAPPING_NOTIFICATION.errorBody'),
          variant: 'error',
        });
        setStep1Progress('readyToSelectFile');
      }
    }

    if (!fileUploader) return;

    setFileName(fileUploader);

    await importConfigurationProcess();
  }, [
    fileName,
    files?.file,
    files?.mappingList,
    handleUploadProgress,
    importConfigurationProcess,
    service,
    setFileName,
    t,
  ]);

  const convertToColumnsProcess = useCallback(() => {
    const aux = mappingList.mappingList
      .map((element: { mapping: ITableMappingField }, index: number) => {
        let format;
        const dataType = element.mapping.formatColumn?.dataType;

        if (dataType === SelectorEnum.country || dataType === SelectorEnum.date) {
          format = element.mapping.formatColumn?.selected?.id;
        } else if (dataType === SelectorEnum.sex) {
          format = element.mapping.formatColumn?.selected?.value;
        }

        return {
          format: format ? format.toString() : null,
          idSpecialField: null,
          idCustomField: element.mapping.mappingField.id,
          columnNumber: (index + 1).toString(),
        } as ColumnProcessProps;
      })
      .filter((column: ColumnProcessProps) => {
        return column.idCustomField !== StateOption.noImport;
      });
    return aux;
  }, [mappingList.mappingList]);

  const resetImport = useCallback(() => {
    setFileName('');
    setFileSuccess(false);
    setFiles({
      step: ImportSteps.import,
      showStateCard: false,
      importLoading: {
        step: 0,
        mode: velocityBar.slow,
        percentage: 0,
        dataCard: getDataCard(),
      },
    });
  }, [setFileName, setFiles]);

  const cancelProcess = () => {
    resetImport();
    navigate({ pathname: `/v2/${RouteEnum.ContactsImport}` });
  };

  const downloadNewFile = () => {
    const file = files?.file;
    if (file && files?.columnsImports) {
      modifyFile(file, files?.columnsImports.excludedColumn);
    }
  };

  const mainEmailColumn = useCallback(() => {
    let position = 0;
    for (const map of mappingList.mappingList) {
      position++;
      if (map.mapping.header === 'email') {
        break;
      }
    }
    return position.toString();
  }, [mappingList.mappingList]);

  const listColumnNames = useCallback(() => {
    const listColumns = mappingList.mappingList
      .map((element: { mapping: ITableMappingField }) => {
        if (Number(element.mapping.mappingField.id) !== StateOption.noImport) {
          return element.mapping.header;
        }
      })
      .filter((element: string | undefined) => {
        return element !== undefined;
      });

    return listColumns;
  }, [mappingList.mappingList]);

  const nextToMappingField = useCallback(() => {
    const listColumns = listColumnNames();
    if (!listColumns) return;
    setFiles({
      ...files,
      columnsImports: { ...files?.columnsImports, columnsImportsNames: listColumns },
      step: ImportSteps.tagsAndGroup,
      importsOptions: {
        ...files?.importsOptions,
        mainEmailColumn: mainEmailColumn(),
        columnsProcess: convertToColumnsProcess(),
        delimiter: delimiter.value as string,
      },
    });
    datadogRum.addAction('ContactsImport.step2Success', {
      email: userData.email,
      accountId: Number(userData.companyId),
    });
  }, [
    convertToColumnsProcess,
    delimiter.value,
    files,
    listColumnNames,
    mainEmailColumn,
    setFiles,
    userData.companyId,
    userData.email,
  ]);

  const saveImport = useCallback(async () => {
    if (files?.state === loadingStatus.ready && files.groups?.selectedGroupsId && files?.process) {
      await service?.import({
        importData: {
          groups: files.groups?.selectedGroupsId,
          idProcess: files?.process,
        },
      });

      service?.incrementLastRefreshContactsValue();
      resetImport();
      setIsOpenModalSuccess(true);
    }
  }, [files?.groups?.selectedGroupsId, files?.process, files?.state, resetImport, service]);

  const analyze = useCallback(async () => {
    const importsOptions = files?.importsOptions;
    const fileNameAux = files?.file?.name ?? fileName;

    if (importsOptions && fileNameAux) {
      const {
        mainEmailColumn: emailColumn,
        columnsProcess,
        delimiter: auxDelimiter,
      } = importsOptions;

      convertToColumnsProcess();
      const fileAnalysis: FileAnalysis | undefined = await service.analyze({
        file: fileNameAux,
        mainEmailColumn: emailColumn,
        columnsProcess: columnsProcess,
        delimiter: auxDelimiter,
        idEncoding: '0',
        reprocessFile: false,
        hasHead: true,
      });
      if (fileAnalysis) {
        const { idProcess: auxIdProcess } = fileAnalysis;
        verifyImportState(auxIdProcess);
      }
    }
  }, [
    convertToColumnsProcess,
    fileName,
    files?.file?.name,
    files?.importsOptions,
    service,
    verifyImportState,
  ]);

  const addTagsAndGroup = useCallback(() => {
    setFiles({ ...files, step: ImportSteps.info, showStateCard: true });
    loadingBar();
    void analyze();
  }, [analyze, files, loadingBar, setFiles]);

  const onDragEnter = () => {
    setIsDraged(true);
  };

  const onDragLeave = () => {
    setIsDraged(false);
  };

  const { getRootProps, open, getInputProps } = useDropzone({
    ...dropConfig,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onDrop,
    onDragEnter,
    onDragLeave,
  });

  const setMappingListOption = useCallback(
    (option: Option, id: string | number, idColumn: number) => {
      const { mappingList: mapList } = mappingList;
      const index =
        +id !== StateOption.noImport
          ? mapList.findIndex((element: { mapping: ITableMappingField }) => {
              return element.mapping.mappingField.id === id;
            })
          : idColumn;
      const replaceOptionIndex = mapList.findIndex((element: { mapping: ITableMappingField }) => {
        return element.mapping.mappingField.id === option.id;
      });
      const matchCustomFieldElement = listCustomField?.find((element: CustomsFieldsProps) => {
        return element.id === String(option.id);
      });

      const newMapping = mapList.at(index)?.mapping;
      if (newMapping) {
        newMapping.mappingField = option;
        newMapping.header = option.name.toString();

        if (matchCustomFieldElement) {
          const dataTypeSelectors = matchValueSelector(matchCustomFieldElement.dataType);
          newMapping.formatColumn = {
            dataType: matchCustomFieldElement.dataType,
            selectors: dataTypeSelectors,
            selected: dataTypeSelectors ? dataTypeSelectors.at(0) : undefined,
          };
        }
        mapList.splice(index, 1, { mapping: newMapping });
      }

      if (replaceOptionIndex !== StateOption.noImport) {
        const replaceOption = mapList.at(replaceOptionIndex)?.mapping;

        if (replaceOption) {
          const test: ITableMappingField = {
            idColumn: replaceOptionIndex,
            data: replaceOption.data,
            mappingField: noImport,
            header: noImport.name.toString(),
          };
          mapList.splice(replaceOptionIndex, 1, { mapping: test });
        }
      }
      setMappingList({ mappingList: mapList });
    },
    [listCustomField, mappingList, noImport]
  );

  const onchangeDataType = useCallback(
    (option: Option, id: string | number) => {
      const { mappingList: mapList } = mappingList;
      const index = mapList.findIndex((element: { mapping: ITableMappingField }) => {
        return element.mapping.mappingField.id === id;
      });
      const bitMapping = mapList.at(index)?.mapping;

      if (bitMapping?.formatColumn) {
        bitMapping.formatColumn.selected = option;
        mapList.splice(index, 1, { mapping: bitMapping });
        setMappingList({ mappingList: mapList });
      }
    },
    [mappingList]
  );

  const selectDelimiter = useCallback((option: Option | Option[]) => {
    setDelimiter(option as Option);
    //TODO: should call configuration endpoint again with selected delimiter
  }, []);

  useEffect(() => {
    if (files?.step !== ImportSteps.mappingField) return;

    if (mappingList && mappingList.mappingList.length !== 0) {
      let noImportColumn = 0;
      const excludedColumn = [];
      let i = 0;

      for (const element of mappingList.mappingList) {
        if (Number(element.mapping.mappingField.id) === StateOption.noImport) {
          noImportColumn++;
          excludedColumn.push(i);
        }
        i++;
      }
      const importColumnCount = mappingList.mappingList.length - noImportColumn;
      setFiles({
        ...files,
        mappingList: mappingList,
        columnsImports: {
          ...files?.columnsImports,
          importColumn: importColumnCount,
          noImportColumn: noImportColumn,
          excludedColumn: excludedColumn,
        },
      });
    } else if (files?.quantityContacts && files.quantityContacts > 0) {
      void importFile(); // TODO: check and remove
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files?.step, mappingList, service, listCustomField]);

  useEffect(() => {
    const { step, columnsImports, groups, file } = files ?? {};
    const fileNameSelected = file?.name ?? fileName;
    const selectedGroupsNames = selectedGroups.filter((g) => g.id !== 0).map((g) => g.name);

    if (step === ImportSteps.info && columnsImports && groups && fileNameSelected) {
      const auxSelectedGroupsName = groups?.selectedGroupsNames?.slice(0, 5) || [''];
      const detail: IDetailViewList[] = convertDataToViewInfo(
        fileNameSelected,
        columnsImports.importColumn,
        auxSelectedGroupsName
      );
      if (detail) {
        setDetailList(detail);
      }
    } else if (step === ImportSteps.tagsAndGroup) {
      const listGroupsSelected = selectedGroups.map((sg: Option) => {
        return sg.id;
      }) as number[];
      setFiles({
        ...files,
        groups: { selectedGroupsId: listGroupsSelected, selectedGroupsNames: selectedGroupsNames },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files?.step, selectedGroups]);

  const onRowClick = useCallback(
    (rowSelected: ImportRecentProps) => {
      setFiles({ ...files, step: ImportSteps.import });
      setFileName(rowSelected.file);
      setFileSuccess(true);
    },
    [files, setFileName, setFiles]
  );

  return (
    <>
      {/* <ImportActionCards /> */}
      <Card>
        <CardSection noPadding>
          <SectionHeader
            withoutDivide
            icon={<UploadArrowIcon />}
            title={
              <Text fontWeight="medium" variant="title-1">
                {t('IMPORT_MAIN.importFile')}
              </Text>
            }
          />
          <Step actualStep={files?.step ?? 0} steps={steps} />
          {files?.step === ImportSteps.import && (
            <>
              <div className="px-10 pb-0 pt-6">
                {/* <Button onClick={simulateUpload}>Simulate upload</Button> */}
                {step1Progress === 'readyToSelectFile' ? (
                  <UploadZone
                    fileName={fileName}
                    inputProps={getInputProps}
                    isDraged={isDraged}
                    rootProps={getRootProps({ className: 'dropzone' })}
                    onClickHandler={open}
                  />
                ) : (
                  <UploadProgress
                    isLoadingMapFields={step1Progress === 'mappingFields'}
                    isLoadingUploadFile={step1Progress === 'uploadingFile'}
                    // isMappingFieldsCompleted={step1Progress === 'mappingFieldsEnd'}
                    percentage={uploadPercentage}
                    text={t(`IMPORT_MAIN.${step1Progress}`)}
                    // onCompletionAnimationEnd={resetStep1Loader}
                    // onUploadFileCompleted={() => {
                    //   setStep1Progress('mappingFields');
                    //   simulateMappingAwait();
                    // }}
                  />
                )}
              </div>
              <Flex column>
                <Flex alignment="end">
                  <div className="px-10 py-6 text-14">{t('IMPORT_MAIN.consent')}</div>
                </Flex>
                <Flex alignment="end" className="border-t border-emblue-borders px-10 py-2">
                  <Button
                    disabled={!fileSuccess}
                    id="import-continue"
                    onClick={() => void importFile()}
                  >
                    {t('IMPORT_MAIN.importContacts')}
                  </Button>
                </Flex>
              </Flex>
            </>
          )}
          {files?.step === ImportSteps.mappingField && (
            <Flex column itemAlignment="stretch">
              <Flex className="px-10 pb-1 pt-2" itemAlignment="center">
                <Flex>
                  <Text variant="text">
                    {fileDataQuantity?.quantityContacts} {t('IMPORT_MAIN.recognizedRegisters')}{' '}
                    {fileDataQuantity?.quantityColumns} {t('IMPORT_MAIN.columns')}
                  </Text>
                </Flex>
                <Flex withGap alignment="end" gapSize="medium">
                  <Text color="primary" fontWeight="normal" variant="sub-headline">
                    {t('IMPORT_MAIN.delimiterCapitalize')}
                  </Text>
                  <div className="w-72">
                    <Select
                      id="import-file-delimiter"
                      options={delimiterList}
                      value={delimiter}
                      onChange={selectDelimiter}
                    />
                  </div>
                </Flex>
              </Flex>
              {selectListCustomField && files?.mappingList && (
                <div className="mt-5 px-4">
                  <ContactsMappingField
                    listCustomField={selectListCustomField}
                    mappingList={files?.mappingList}
                    onchangeDataType={onchangeDataType}
                    setMappingList={setMappingListOption}
                  />
                </div>
              )}
              <Flex column alignment="end" itemAlignment="end">
                <Flex alignment="end" className="border-emblue-border flex border-y py-6 pr-10">
                  {files?.columnsImports && (
                    <Text variant="text">
                      {t('IMPORT_MAIN.isImport')}{' '}
                      <span className="font-medium text-emblue-primary">
                        {files?.columnsImports.importColumn}
                      </span>{' '}
                      {t('IMPORT_MAIN.columns')}
                    </Text>
                  )}
                  {files?.columnsImports && files?.columnsImports.noImportColumn !== 0 && (
                    <Text variant="text">
                      <span className="font-medium text-emblue-primary">
                        , {files?.columnsImports.noImportColumn}
                      </span>{' '}
                      {t('IMPORT_MAIN.notImport')}
                    </Text>
                  )}
                </Flex>
                <Flex withGap alignment="end" className="py-2 pr-10" gapSize="small">
                  <Button outline secondary id="import-cancel" onClick={cancelProcess}>
                    {t('IMPORT_MAIN.cancel')}
                  </Button>
                  <Button id="import-continue" onClick={nextToMappingField}>
                    {t('IMPORT_MAIN.next')}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          )}
          {files?.step === ImportSteps.tagsAndGroup && (
            <Flex column>
              <div className="w-full p-4 px-10">
                <ContactsTagsAndGroups
                  selectedGroups={selectedGroups}
                  setSelectedGroups={setSelectedGroups}
                />
              </div>
              <Flex
                withGap
                alignment="end"
                className="border-emblue-border w-full border-t px-10 py-2"
                gapSize="small"
              >
                <Button outline secondary id="import-cancel" onClick={cancelProcess}>
                  {t('IMPORT_MAIN.cancel')}
                </Button>
                <Button id="import-continue" onClick={addTagsAndGroup}>
                  {t('IMPORT_MAIN.next')}
                </Button>
              </Flex>
            </Flex>
          )}
          {files?.step === ImportSteps.info && (
            <>
              <div className="p-6 pb-10">
                <ContactsViewInfo analysisResult={files?.analysisResult} />
              </div>
              <div className="w-full border-t border-emblue-borders px-10 py-2">
                <Flex column alignment="end" itemAlignment="end">
                  <Flex withGap alignment="end" gapSize="small">
                    <Button
                      outline
                      secondary
                      disabled={!(files.state === loadingStatus.ready)}
                      iconLeft={
                        <IconSvg strokeColor="secondary" svgComponent={<ExportUserIcon />} />
                      }
                      id="import-csv-file-export"
                      onClick={downloadNewFile}
                    >
                      {t('IMPORT_MAIN.toExport')}
                    </Button>
                    <Button
                      outline
                      secondary
                      disabled={!files.state || files.state === loadingStatus.loading}
                      id="import-cancel"
                      onClick={cancelProcess}
                    >
                      {t('IMPORT_MAIN.cancel')}
                    </Button>
                    <Button
                      disabled={!(files.state === loadingStatus.ready)}
                      id="import-continue"
                      onClick={() => void saveImport()}
                    >
                      {t('IMPORT_MAIN.confirmImportation')}
                    </Button>
                  </Flex>
                </Flex>
              </div>
            </>
          )}
        </CardSection>
      </Card>
      {files?.step === ImportSteps.import && recentImportIsEnabled && (
        <Card className="mt-6">
          <CardSection noPadding>
            <SectionHeader
              withoutDivide
              icon={<UploadArrowIcon />}
              title={
                <Text fontWeight="medium" variant="title-1">
                  {t('IMPORT_MAIN.importRecent')}
                </Text>
              }
            />
            <Flex column withGap alignment="start" className="px-4 pt-4" gapSize="medium">
              {importRecentFiles && (
                <ImportRecentTable importRecentList={importRecentFiles} onRowClick={onRowClick} />
              )}
            </Flex>
          </CardSection>
        </Card>
      )}
      {files?.step === ImportSteps.info && detailList && (
        <Card className="mt-6 rounded-t-none px-4 pt-4">
          <CardSection noPadding>
            <ContactsDetailTable
              detailList={detailList}
              setIsOpenModalMappedFields={setIsOpenModalMappedFields}
            />
          </CardSection>
        </Card>
      )}
      <ImportSuccessModal
        setShowModalSuccess={setIsOpenModalSuccess}
        showModalSuccess={isOpenModalSuccess}
      />
      {files?.columnsImports?.columnsImportsNames && (
        <MappedFieldModal
          isOpenModalMappedFields={isOpenModalMappedFields}
          listMappedField={files?.columnsImports?.columnsImportsNames}
          setIsOpenModalMappedFields={setIsOpenModalMappedFields}
        />
      )}
    </>
  );
};
